<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';
import KanbanNotesAPI from 'dashboard/api/kanbanNotes';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['close', 'duplicated']);

const store = useStore();
const { t } = useI18n();
const showAlert = useAlert();

const title = ref('');
const targetPipelineId = ref(null);
const targetColumnId = ref(null);
const targetStatus = ref('open');
const targetAssigneeId = ref(null);
const targetWatcherIds = ref([]);
const targetTags = ref([]);
const copyNotes = ref(true);
const notesCount = ref(0);
const isDuplicating = ref(false);

const pipelines = computed(() => store.getters['kanban/pipelines'] ?? []);
const agents = computed(() => store.getters['agents/getAgents'] ?? []);
const accountLabels = computed(() => store.getters['labels/getLabels'] ?? []);

const targetPipelineColumns = computed(() => {
  const p = pipelines.value.find(p => p.id === targetPipelineId.value);
  return p?.columns ?? [];
});

watch(targetPipelineId, newPid => {
  const cols = pipelines.value.find(p => p.id === newPid)?.columns ?? [];
  targetColumnId.value = cols[0]?.id ?? null;
});

const statusOptions = [
  { value: 'open', label: t('KANBAN.CONTACT_SECTION.STATUS_OPEN') },
  { value: 'won', label: t('KANBAN.CONTACT_SECTION.STATUS_WON') },
  { value: 'lost', label: t('KANBAN.CONTACT_SECTION.STATUS_LOST') },
];

const assigneeOpen = ref(false);
const assigneeSearch = ref('');

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase().trim();
  if (!q) return agents.value;
  return agents.value.filter(a => a.name.toLowerCase().includes(q));
});

const selectedAgentName = computed(() => {
  if (!targetAssigneeId.value)
    return t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_ASSIGNEE_NONE');
  return (
    agents.value.find(a => a.id === targetAssigneeId.value)?.name ??
    t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_ASSIGNEE_NONE')
  );
});

function selectAssignee(id) {
  targetAssigneeId.value = id;
  assigneeOpen.value = false;
  assigneeSearch.value = '';
}

const watchersOpen = ref(false);
const watchersSearch = ref('');

const filteredWatcherAgents = computed(() => {
  const q = watchersSearch.value.toLowerCase().trim();
  const candidates = agents.value.filter(
    a => !targetWatcherIds.value.includes(a.id)
  );
  if (!q) return candidates;
  return candidates.filter(a => a.name.toLowerCase().includes(q));
});

const selectedWatcherNames = computed(() =>
  targetWatcherIds.value
    .map(id => agents.value.find(a => a.id === id)?.name ?? '')
    .filter(Boolean)
);

function toggleWatcher(id) {
  const idx = targetWatcherIds.value.indexOf(id);
  if (idx === -1) targetWatcherIds.value = [...targetWatcherIds.value, id];
  else targetWatcherIds.value = targetWatcherIds.value.filter(w => w !== id);
}

function removeWatcher(id) {
  targetWatcherIds.value = targetWatcherIds.value.filter(w => w !== id);
}

const tagsOpen = ref(false);
const tagSearch = ref('');

const allLabelTitles = computed(() =>
  (accountLabels.value ?? []).map(l => l.title)
);

const filteredLabelOptions = computed(() => {
  const q = tagSearch.value.toLowerCase().trim();
  if (!q) return allLabelTitles.value;
  return allLabelTitles.value.filter(l => l.toLowerCase().includes(q));
});

function toggleTag(tag) {
  const idx = targetTags.value.indexOf(tag);
  if (idx === -1) targetTags.value = [...targetTags.value, tag];
  else targetTags.value = targetTags.value.filter(t => t !== tag);
}

function removeTag(tag) {
  targetTags.value = targetTags.value.filter(t => t !== tag);
}

function tagColor(label) {
  const found = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (found?.color) return { bg: `${found.color}22`, text: found.color };
  return { bg: 'rgba(99,102,241,0.12)', text: '#4338ca' };
}

async function loadNotesCount() {
  if (!props.card.id) return;
  try {
    const { data } = await KanbanNotesAPI.list(props.card.id);
    const notes = Array.isArray(data) ? data : (data.data ?? []);
    notesCount.value = notes.length;
  } catch {
    notesCount.value = 0;
  }
}

async function duplicate() {
  if (isDuplicating.value) return;
  isDuplicating.value = true;
  try {
    const { data: createResp } = await KanbanAPI.createCard({
      pipeline_id: targetPipelineId.value,
      contact_id: props.card.contact?.id,
      title: title.value || null,
      assignee_id: targetAssigneeId.value || null,
    });
    const newCard = createResp?.data ?? createResp;
    const newCardId = newCard.id;

    if (
      targetColumnId.value !== newCard.column_id ||
      targetStatus.value !== 'open'
    ) {
      await KanbanAPI.updateCard(newCardId, {
        column_id: targetColumnId.value,
        status: targetStatus.value,
      });
    }

    const srcAttrs = { ...(props.card.custom_attributes ?? {}) };
    srcAttrs.watchers = targetWatcherIds.value;
    await KanbanAPI.updateCard(newCardId, {
      custom_attributes: srcAttrs,
      value: props.card.value ?? null,
      closing_date: props.card.closing_date ?? null,
      closing_time: props.card.closing_time ?? null,
    });

    if (targetTags.value.length > 0) {
      const existingLabels = props.card.contact?.labels ?? [];
      const merged = [...new Set([...existingLabels, ...targetTags.value])];
      await ContactAPI.updateContactLabels(props.card.contact?.id, merged);
    }

    if (copyNotes.value && notesCount.value > 0) {
      const { data: notesResp } = await KanbanNotesAPI.list(props.card.id);
      const notes = Array.isArray(notesResp)
        ? notesResp
        : (notesResp.data ?? []);
      await Promise.all(
        notes.map(note => {
          const formData = new FormData();
          formData.append('content', note.content ?? '');
          return KanbanNotesAPI.create(newCardId, formData);
        })
      );
    }

    await store.dispatch('kanban/fetchCards');
    showAlert(t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_SUCCESS'));
    emit('duplicated', newCardId);
  } catch {
    showAlert(t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_ERROR'));
  } finally {
    isDuplicating.value = false;
  }
}

function closeAllDropdowns(e) {
  if (!e.target.closest('[data-dup-dropdown]')) {
    assigneeOpen.value = false;
    watchersOpen.value = false;
    tagsOpen.value = false;
  }
}

onMounted(async () => {
  title.value = `Cópia de ${props.card.title || props.card.contact?.name || ''}`;
  targetPipelineId.value = props.card.pipeline_id ?? null;
  targetColumnId.value = props.card.column_id ?? null;
  targetStatus.value = props.card.status ?? 'open';
  targetAssigneeId.value = props.card.assignee_id ?? null;
  targetWatcherIds.value = [...(props.card.custom_attributes?.watchers ?? [])];
  targetTags.value = [...(props.card.contact?.labels ?? [])];
  store.dispatch('labels/get');
  document.addEventListener('mousedown', closeAllDropdowns);
  await loadNotesCount();
});

onUnmounted(() => {
  document.removeEventListener('mousedown', closeAllDropdowns);
});
</script>

<template>
  <div
    class="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-background rounded-xl shadow-2xl w-full max-w-[520px] mx-4 flex flex-col overflow-hidden"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
      >
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_TITLE') }}
        </h2>
        <button
          class="text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Body -->
      <div class="px-5 py-4 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
        <!-- Título -->
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_TITLE_LABEL') }}
          </label>
          <input
            v-model="title"
            type="text"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </div>

        <!-- Pipeline + Coluna -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_PIPELINE_LABEL') }}
            </label>
            <select
              v-model="targetPipelineId"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option v-for="p in pipelines" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_COLUMN_LABEL') }}
            </label>
            <select
              v-model="targetColumnId"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option
                v-for="col in targetPipelineColumns"
                :key="col.id"
                :value="col.id"
              >
                {{ col.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Status + Responsável -->
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_STATUS_LABEL') }}
            </label>
            <select
              v-model="targetStatus"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <!-- Responsável dropdown -->
          <div class="flex flex-col gap-1 relative" data-dup-dropdown>
            <label class="text-xs font-medium text-n-slate-11">
              {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_ASSIGNEE_LABEL') }}
            </label>
            <button
              data-dup-dropdown
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm text-n-slate-12 text-left focus:outline-none focus:border-n-brand flex items-center justify-between"
              @click.stop="assigneeOpen = !assigneeOpen"
            >
              <span class="truncate">{{ selectedAgentName }}</span>
              <span
                class="i-lucide-chevron-down size-4 text-n-slate-10 flex-shrink-0"
              />
            </button>
            <div
              v-if="assigneeOpen"
              data-dup-dropdown
              class="absolute top-full mt-1 left-0 right-0 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-40 overflow-y-auto"
              @click.stop
            >
              <input
                v-model="assigneeSearch"
                type="text"
                class="w-full px-3 py-1.5 text-sm border-b border-n-weak bg-transparent focus:outline-none"
                placeholder="Buscar..."
              />
              <button
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-alpha-1 transition-colors text-n-slate-11"
                :class="{ 'text-n-brand font-medium': !targetAssigneeId }"
                @click="selectAssignee(null)"
              >
                {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_ASSIGNEE_NONE') }}
              </button>
              <button
                v-for="agent in filteredAgents"
                :key="agent.id"
                class="w-full px-3 py-2 text-sm text-left hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                :class="{
                  'text-n-brand font-medium': targetAssigneeId === agent.id,
                }"
                @click="selectAssignee(agent.id)"
              >
                {{ agent.name }}
              </button>
            </div>
          </div>
        </div>

        <!-- Seguidores -->
        <div class="flex flex-col gap-1 relative" data-dup-dropdown>
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_WATCHERS_LABEL') }}
          </label>
          <div
            data-dup-dropdown
            class="min-h-[38px] w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm flex flex-wrap gap-1 cursor-pointer"
            @click.stop="watchersOpen = !watchersOpen"
          >
            <span
              v-for="name in selectedWatcherNames"
              :key="name"
              class="inline-flex items-center gap-1 bg-n-alpha-2 text-n-slate-12 text-xs px-2 py-0.5 rounded-full"
            >
              {{ name }}
              <span
                class="i-lucide-x size-3 text-n-slate-10 hover:text-n-slate-12 cursor-pointer"
                @click.stop="
                  removeWatcher(agents.find(a => a.name === name)?.id)
                "
              />
            </span>
            <span
              v-if="selectedWatcherNames.length === 0"
              class="text-n-slate-10"
              >—</span>
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-10 ml-auto flex-shrink-0 self-center"
            />
          </div>
          <div
            v-if="watchersOpen"
            data-dup-dropdown
            class="absolute top-full mt-1 left-0 right-0 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-40 overflow-y-auto"
            @click.stop
          >
            <input
              v-model="watchersSearch"
              type="text"
              class="w-full px-3 py-1.5 text-sm border-b border-n-weak bg-transparent focus:outline-none"
              placeholder="Buscar..."
            />
            <button
              v-for="agent in filteredWatcherAgents"
              :key="agent.id"
              class="w-full px-3 py-2 text-sm text-left hover:bg-n-alpha-1 transition-colors text-n-slate-12 flex items-center justify-between"
              @click="toggleWatcher(agent.id)"
            >
              {{ agent.name }}
              <span
                v-if="targetWatcherIds.includes(agent.id)"
                class="i-lucide-check size-3 text-n-brand"
              />
            </button>
          </div>
        </div>

        <!-- Tags -->
        <div class="flex flex-col gap-1 relative" data-dup-dropdown>
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_TAGS_LABEL') }}
          </label>
          <div
            data-dup-dropdown
            class="min-h-[38px] w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm flex flex-wrap gap-1 cursor-pointer"
            @click.stop="tagsOpen = !tagsOpen"
          >
            <span
              v-for="tag in targetTags"
              :key="tag"
              class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
              :style="{
                backgroundColor: tagColor(tag).bg,
                color: tagColor(tag).text,
              }"
            >
              {{ tag }}
              <span
                class="i-lucide-x size-3 cursor-pointer hover:opacity-70"
                @click.stop="removeTag(tag)"
              />
            </span>
            <span v-if="targetTags.length === 0"
class="text-n-slate-10"
              >—</span>
            <span
              class="i-lucide-chevron-down size-4 text-n-slate-10 ml-auto flex-shrink-0 self-center"
            />
          </div>
          <div
            v-if="tagsOpen"
            data-dup-dropdown
            class="absolute top-full mt-1 left-0 right-0 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-40 overflow-y-auto"
            @click.stop
          >
            <input
              v-model="tagSearch"
              type="text"
              class="w-full px-3 py-1.5 text-sm border-b border-n-weak bg-transparent focus:outline-none"
              placeholder="Buscar..."
            />
            <button
              v-for="label in filteredLabelOptions"
              :key="label"
              class="w-full px-3 py-2 text-sm text-left hover:bg-n-alpha-1 transition-colors text-n-slate-12 flex items-center justify-between"
              @click="toggleTag(label)"
            >
              {{ label }}
              <span
                v-if="targetTags.includes(label)"
                class="i-lucide-check size-3 text-n-brand"
              />
            </button>
          </div>
        </div>

        <!-- Copiar notas -->
        <label class="flex items-center gap-2 cursor-pointer select-none">
          <input
            v-model="copyNotes"
            type="checkbox"
            class="size-4 accent-n-brand rounded"
          />
          <span class="text-sm text-n-slate-12">
            {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_COPY_NOTES') }}
            <span v-if="notesCount > 0" class="text-n-slate-10 ml-1">
              ({{
                t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_NOTES_COUNT', {
                  count: notesCount,
                })
              }})
            </span>
          </span>
        </label>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak"
      >
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
          :disabled="isDuplicating"
          @click="emit('close')"
        >
          {{ t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_CANCEL') }}
        </button>
        <button
          class="px-4 py-2 rounded-lg text-sm font-medium bg-n-brand text-white hover:brightness-110 transition-all disabled:opacity-50"
          :disabled="isDuplicating || !targetPipelineId || !targetColumnId"
          @click="duplicate"
        >
          <span v-if="isDuplicating">{{
            t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_DUPLICATING')
          }}</span>
          <span v-else>{{
            t('KANBAN.OPPORTUNITY_MODAL.DUPLICATE_CONFIRM')
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
